/**
 * Standalone Articles - Uro2Go
 * 
 * General patient education articles that don't belong to a specific category.
 * All content at 8th-grade reading level.
 * Both English and Spanish versions provided.
 */

export const standaloneArticles = [
  {
    id: 'bosniak-3-cysts',
    title: {
      en: 'Bosniak 3 Kidney Cysts – Understanding Your Options',
      es: 'Quistes Renales Bosniak 3 – Entendiendo Sus Opciones'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/9/98/Bosniak_2F_renal_cyst.jpg',
      alt: {
        en: 'CT scan showing a complex kidney cyst',
        es: 'Tomografía computarizada mostrando un quiste renal complejo'
      },
      source: 'Wikimedia Commons (CC BY-SA 4.0)',
      caption: {
        en: 'CT imaging is used to classify kidney cysts and guide management decisions',
        es: 'Las imágenes de TC se usan para clasificar los quistes renales y guiar las decisiones de manejo'
      }
    },
    tags: ['bosniak', 'kidney cyst', 'renal cyst', 'surveillance', 'active surveillance', 'complex cyst'],
    relatedTopics: ['kidney-cancer', 'kidney'],
    whenToCall: {
      en: [
        'New or worsening flank pain',
        'Blood in your urine (hematuria)',
        'Unexplained weight loss',
        'Fever without another cause',
        'Any new concerning symptoms'
      ],
      es: [
        'Dolor de costado nuevo o que empeora',
        'Sangre en la orina (hematuria)',
        'Pérdida de peso inexplicable',
        'Fiebre sin otra causa',
        'Cualquier síntoma nuevo preocupante'
      ]
    },
    content: {
      en: `A Bosniak 3 cyst is a type of complex kidney cyst that requires careful evaluation. While some of these cysts are benign (not cancer), others may be malignant (cancerous). Your urologist will help you understand your specific situation and the best approach for you.

**What Is the Bosniak Classification?**

The Bosniak classification system helps doctors categorize kidney cysts based on how they appear on CT or MRI scans. The categories range from 1 (simple, benign cyst) to 4 (likely cancerous). Bosniak 3 cysts fall in the "indeterminate" category, meaning they have features that make them suspicious but not definitively cancerous.

**Bosniak 3 Cyst Characteristics:**
• Thickened or irregular walls (3-4 mm or more)
• Thickened or irregular internal dividers (septa)
• Measurable enhancement on contrast imaging
• About 40-60% of Bosniak 3 cysts turn out to be cancerous when removed

---

**Management Options**

Modern guidelines recognize that not all Bosniak 3 cysts need immediate surgery. Your options typically include:

**1. Active Surveillance (Monitoring)**
Active surveillance means watching the cyst closely with regular imaging rather than treating it right away. This may be recommended if:

• The cyst is small (especially less than 2-3 cm)
• You have other health conditions that make surgery risky
• You prefer to avoid surgery if possible
• Your life expectancy or overall health suggests watching is reasonable

**Surveillance typically involves:**
• Repeat CT or MRI scan in 3-6 months
• If stable, imaging every 6-12 months
• Watching for growth or changes in appearance
• Discussing any new symptoms with your doctor

**2. Surgical Removal (Partial Nephrectomy)**
Surgery to remove the cyst along with a margin of normal kidney tissue may be recommended if:

• The cyst is larger than 4 cm
• The cyst shows worrisome features like wall irregularity
• You are young and healthy with good life expectancy
• You prefer definitive treatment
• The cyst is growing on surveillance imaging

**3. Kidney Biopsy**
In some cases, a needle biopsy may help determine if the cyst is cancerous. However, biopsies of cystic lesions can be challenging and may not always provide a clear answer.

---

**What the Guidelines Say**

Recent guidelines from major urology organizations (AUA, EAU, CUA) have shifted toward offering active surveillance more often for Bosniak 3 cysts, especially smaller ones. This reflects our understanding that:

• Many Bosniak 3 cysts grow very slowly or not at all
• Even when cancerous, small cystic kidney tumors rarely spread
• Surgery carries its own risks that must be weighed
• Overtreatment can cause unnecessary harm

---

**Questions to Ask Your Doctor**

• What specific features does my cyst have?
• What is the estimated risk that this cyst is cancerous?
• Am I a good candidate for active surveillance?
• How often would I need imaging if we watch it?
• What would make you recommend surgery in the future?
• What are the risks of surgery in my situation?

---

**Important Points to Remember**

• A Bosniak 3 classification does NOT mean you definitely have cancer
• Active surveillance is a legitimate, guideline-supported option
• The decision should be made together with your urologist based on your individual situation
• If you choose surveillance, keeping your imaging appointments is essential
• Most cystic kidney cancers, even if present, are slow-growing and treatable`,

      es: `Un quiste Bosniak 3 es un tipo de quiste renal complejo que requiere una evaluación cuidadosa. Mientras algunos de estos quistes son benignos (no son cáncer), otros pueden ser malignos (cancerosos). Su urólogo le ayudará a entender su situación específica y el mejor enfoque para usted.

**¿Qué Es la Clasificación de Bosniak?**

El sistema de clasificación de Bosniak ayuda a los médicos a categorizar los quistes renales según cómo aparecen en las tomografías computarizadas o resonancias magnéticas. Las categorías van del 1 (quiste simple, benigno) al 4 (probablemente canceroso). Los quistes Bosniak 3 caen en la categoría "indeterminada", lo que significa que tienen características que los hacen sospechosos pero no definitivamente cancerosos.

**Características del Quiste Bosniak 3:**
• Paredes engrosadas o irregulares (3-4 mm o más)
• Divisores internos (septos) engrosados o irregulares
• Realce medible en imágenes con contraste
• Aproximadamente 40-60% de los quistes Bosniak 3 resultan ser cancerosos cuando se extirpan

---

**Opciones de Manejo**

Las guías modernas reconocen que no todos los quistes Bosniak 3 necesitan cirugía inmediata. Sus opciones típicamente incluyen:

**1. Vigilancia Activa (Monitoreo)**
La vigilancia activa significa observar el quiste de cerca con imágenes regulares en lugar de tratarlo de inmediato. Esto puede recomendarse si:

• El quiste es pequeño (especialmente menos de 2-3 cm)
• Tiene otras condiciones de salud que hacen la cirugía riesgosa
• Prefiere evitar la cirugía si es posible
• Su expectativa de vida o salud general sugiere que observar es razonable

**La vigilancia típicamente involucra:**
• Repetir TC o RM en 3-6 meses
• Si es estable, imágenes cada 6-12 meses
• Vigilar el crecimiento o cambios en apariencia
• Discutir cualquier síntoma nuevo con su médico

**2. Extirpación Quirúrgica (Nefrectomía Parcial)**
La cirugía para extirpar el quiste junto con un margen de tejido renal normal puede recomendarse si:

• El quiste es mayor de 4 cm
• El quiste muestra características preocupantes como irregularidad de la pared
• Es joven y saludable con buena expectativa de vida
• Prefiere tratamiento definitivo
• El quiste está creciendo en las imágenes de vigilancia

**3. Biopsia Renal**
En algunos casos, una biopsia con aguja puede ayudar a determinar si el quiste es canceroso. Sin embargo, las biopsias de lesiones quísticas pueden ser difíciles y no siempre proporcionan una respuesta clara.

---

**Puntos Importantes para Recordar**

• Una clasificación Bosniak 3 NO significa que definitivamente tenga cáncer
• La vigilancia activa es una opción legítima respaldada por las guías
• La decisión debe tomarse junto con su urólogo basándose en su situación individual
• Si elige vigilancia, asistir a sus citas de imágenes es esencial
• La mayoría de los cánceres renales quísticos, incluso si están presentes, son de crecimiento lento y tratables`
    },
    keyPoints: {
      en: [
        'Bosniak 3 cysts are "indeterminate" - not definitely cancer',
        'About 40-60% turn out to be cancerous when removed',
        'Active surveillance is a guideline-supported option',
        'Surgery may be recommended for larger or growing cysts',
        'Decision should be individualized with your urologist'
      ],
      es: [
        'Los quistes Bosniak 3 son "indeterminados" - no definitivamente cáncer',
        'Aproximadamente 40-60% resultan ser cancerosos cuando se extirpan',
        'La vigilancia activa es una opción respaldada por las guías',
        'La cirugía puede recomendarse para quistes más grandes o en crecimiento',
        'La decisión debe individualizarse con su urólogo'
      ]
    }
  },
  {
    id: 'small-renal-mass',
    title: {
      en: 'Small Renal Masses (<3 cm) – Active Surveillance Option',
      es: 'Masas Renales Pequeñas (<3 cm) – Opción de Vigilancia Activa'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/c/c4/Axial_CT_scan_of_left_renal_cortical_cyst.png',
      alt: {
        en: 'CT scan showing a small renal mass',
        es: 'Tomografía computarizada mostrando una masa renal pequeña'
      },
      source: 'Wikimedia Commons (CC BY-SA 4.0)',
      caption: {
        en: 'CT imaging helps characterize small kidney masses and guide treatment decisions',
        es: 'Las imágenes de TC ayudan a caracterizar las masas renales pequeñas y guiar las decisiones de tratamiento'
      }
    },
    tags: ['small renal mass', 'kidney tumor', 'active surveillance', 'SRM', 'renal cell carcinoma', 'watchful waiting'],
    relatedTopics: ['kidney-cancer', 'kidney'],
    whenToCall: {
      en: [
        'New or worsening flank or back pain',
        'Blood in your urine',
        'Unexplained weight loss or fatigue',
        'Fever without another explanation',
        'New lump or swelling in your abdomen',
        'Missed or overdue surveillance imaging'
      ],
      es: [
        'Dolor de costado o espalda nuevo o que empeora',
        'Sangre en la orina',
        'Pérdida de peso o fatiga inexplicable',
        'Fiebre sin otra explicación',
        'Nuevo bulto o hinchazón en su abdomen',
        'Imágenes de vigilancia perdidas o atrasadas'
      ]
    },
    content: {
      en: `A small renal mass (SRM) is a growth on the kidney that is typically less than 4 cm in size. When a small mass is found on your kidney—often incidentally during imaging for another reason—it can be concerning. However, not all small kidney masses are cancer, and even those that are cancerous often behave very differently from larger, more aggressive tumors.

**What Is a Small Renal Mass?**

A small renal mass is defined as a solid or partially solid growth on the kidney, usually less than 4 cm (about 1.5 inches). For masses less than 3 cm, the options for management are particularly flexible.

**Key Facts About Small Renal Masses:**
• About 20-30% of small renal masses turn out to be benign (not cancer)
• Even when cancerous, most small renal masses are low-grade and slow-growing
• Small kidney cancers rarely spread to other parts of the body
• Many small renal masses are found incidentally on scans done for other reasons

---

**Management Options**

Modern guidelines recognize that immediate surgery is not always necessary for small renal masses. Your options include:

**1. Active Surveillance (Monitoring)**

Active surveillance means carefully watching the mass with regular imaging instead of treating it right away. The American Urological Association (AUA) guidelines specifically support this approach for masses less than 2 cm, and it may be appropriate for masses up to 3-4 cm in selected patients.

**Active surveillance may be recommended if:**
• The mass is small (especially less than 2 cm)
• You have other health conditions that increase surgical risk
• You have limited life expectancy
• You have reduced kidney function and want to preserve kidney tissue
• You prefer to avoid surgery if medically appropriate
• A biopsy shows a low-risk tumor type

**Surveillance Protocol:**
• Initial repeat imaging (CT or MRI) at 3-6 months
• If stable, imaging every 6-12 months
• Annual chest X-ray may be recommended
• Regular check-ins with your urologist

**Triggers for Intervention:**
• Tumor growth greater than 0.5 cm per year
• Tumor reaches 4 cm in size
• Development of concerning features on imaging
• Patient preference to proceed with treatment
• Evidence of spread (rare with small masses)

**2. Partial Nephrectomy (Nephron-Sparing Surgery)**

Surgery to remove only the tumor while preserving the rest of the kidney. This is often the preferred surgical approach for small masses because it maintains kidney function.

**3. Thermal Ablation**

Minimally invasive procedures that destroy the tumor using extreme heat (radiofrequency ablation) or cold (cryoablation). These are particularly effective for tumors less than 3 cm and may be good options for patients who want treatment but are not ideal surgical candidates.

**4. Radical Nephrectomy**

Complete removal of the kidney. This is generally not recommended for small masses when the other treatments are feasible, as preserving kidney tissue is important for long-term health.

---

**The Role of Kidney Biopsy**

Your doctor may recommend a needle biopsy of the mass to help guide management. A biopsy can:

• Confirm whether the mass is cancerous or benign
• Identify the specific type of kidney cancer if present
• Help determine how aggressive the tumor may be
• Guide the decision between surveillance and treatment

About 20% of biopsied small renal masses turn out to be benign, potentially avoiding unnecessary surgery.

---

**What the Guidelines Say**

The AUA (American Urological Association) 2021 guidelines state:

*"Clinicians may elect active surveillance with potential for delayed intervention as initial management for solid renal masses <2 cm or predominantly cystic masses (Bosniak 3/4) after shared decision making."*

For masses 2-3 cm, the decision is more individualized, but surveillance remains appropriate for many patients, especially those with:
• Significant health problems (comorbidities)
• Older age with limited life expectancy
• Poor kidney function
• Strong preference to avoid surgery

---

**Growth Rates and Risk**

Studies of small renal masses under surveillance show:

• Average growth rate: about 0.3 cm per year
• Many masses show no growth at all over years of follow-up
• Progression to metastatic disease (spread) is rare (<2%)
• If intervention becomes needed, it is rarely "too late"

---

**Questions to Ask Your Doctor**

• What is the size of my kidney mass?
• Would a biopsy help clarify what type of growth this is?
• Am I a good candidate for active surveillance?
• How often would I need imaging?
• What findings would prompt you to recommend treatment?
• If I need treatment later, what are my options?
• How would surveillance vs. treatment affect my kidney function?

---

**Important Points to Remember**

• Finding a small kidney mass does NOT mean you need immediate surgery
• Active surveillance is a guideline-supported option, not "doing nothing"
• Many small masses never need treatment
• Even if treatment becomes necessary later, outcomes remain excellent
• The decision should be made together with your urologist based on your individual health, preferences, and the characteristics of the mass
• Keeping your surveillance appointments is essential if you choose monitoring`,

      es: `Una masa renal pequeña (MRP) es un crecimiento en el riñón que típicamente mide menos de 4 cm de tamaño. Cuando se encuentra una masa pequeña en su riñón—a menudo incidentalmente durante imágenes por otra razón—puede ser preocupante. Sin embargo, no todas las masas renales pequeñas son cáncer, e incluso aquellas que son cancerosas a menudo se comportan muy diferente de tumores más grandes y agresivos.

**¿Qué Es una Masa Renal Pequeña?**

Una masa renal pequeña se define como un crecimiento sólido o parcialmente sólido en el riñón, usualmente menor de 4 cm (aproximadamente 1.5 pulgadas). Para masas menores de 3 cm, las opciones de manejo son particularmente flexibles.

**Datos Clave Sobre las Masas Renales Pequeñas:**
• Aproximadamente 20-30% de las masas renales pequeñas resultan ser benignas (no cáncer)
• Incluso cuando son cancerosas, la mayoría de las masas renales pequeñas son de bajo grado y crecimiento lento
• Los cánceres renales pequeños raramente se diseminan a otras partes del cuerpo
• Muchas masas renales pequeñas se encuentran incidentalmente en escaneos hechos por otras razones

---

**Opciones de Manejo**

Las guías modernas reconocen que la cirugía inmediata no siempre es necesaria para las masas renales pequeñas. Sus opciones incluyen:

**1. Vigilancia Activa (Monitoreo)**

La vigilancia activa significa observar cuidadosamente la masa con imágenes regulares en lugar de tratarla de inmediato. Las guías de la Asociación Americana de Urología (AUA) específicamente apoyan este enfoque para masas menores de 2 cm, y puede ser apropiado para masas hasta de 3-4 cm en pacientes seleccionados.

**La vigilancia activa puede recomendarse si:**
• La masa es pequeña (especialmente menor de 2 cm)
• Tiene otras condiciones de salud que aumentan el riesgo quirúrgico
• Tiene expectativa de vida limitada
• Tiene función renal reducida y quiere preservar tejido renal
• Prefiere evitar la cirugía si es médicamente apropiado
• Una biopsia muestra un tipo de tumor de bajo riesgo

**Protocolo de Vigilancia:**
• Imágenes repetidas iniciales (TC o RM) a los 3-6 meses
• Si es estable, imágenes cada 6-12 meses
• Puede recomendarse radiografía de tórax anual
• Visitas regulares con su urólogo

**Desencadenantes para Intervención:**
• Crecimiento del tumor mayor de 0.5 cm por año
• El tumor alcanza 4 cm de tamaño
• Desarrollo de características preocupantes en las imágenes
• Preferencia del paciente de proceder con tratamiento
• Evidencia de diseminación (rara con masas pequeñas)

**2. Nefrectomía Parcial (Cirugía Preservadora de Nefronas)**

Cirugía para remover solo el tumor mientras se preserva el resto del riñón. Este es a menudo el enfoque quirúrgico preferido para masas pequeñas porque mantiene la función renal.

**3. Ablación Térmica**

Procedimientos mínimamente invasivos que destruyen el tumor usando calor extremo (ablación por radiofrecuencia) o frío (crioablación). Estos son particularmente efectivos para tumores menores de 3 cm.

---

**Puntos Importantes para Recordar**

• Encontrar una masa renal pequeña NO significa que necesite cirugía inmediata
• La vigilancia activa es una opción respaldada por las guías, no es "no hacer nada"
• Muchas masas pequeñas nunca necesitan tratamiento
• Incluso si el tratamiento se vuelve necesario después, los resultados permanecen excelentes
• La decisión debe tomarse junto con su urólogo basándose en su salud individual, preferencias y las características de la masa
• Asistir a sus citas de vigilancia es esencial si elige el monitoreo`
    },
    keyPoints: {
      en: [
        'About 20-30% of small renal masses are benign (not cancer)',
        'Active surveillance is guideline-supported for masses <2cm',
        'Average growth rate is only about 0.3 cm per year',
        'Progression to metastatic disease is rare (<2%)',
        'Treatment options include partial nephrectomy and ablation'
      ],
      es: [
        'Aproximadamente 20-30% de las masas renales pequeñas son benignas',
        'La vigilancia activa está respaldada por guías para masas <2cm',
        'La tasa de crecimiento promedio es solo 0.3 cm por año',
        'La progresión a enfermedad metastásica es rara (<2%)',
        'Las opciones de tratamiento incluyen nefrectomía parcial y ablación'
      ]
    }
  }
];

// Export list of article IDs for routing
export const standaloneArticleIds = standaloneArticles.map(a => a.id);
